import React, { useRef, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { AiChatSourceReference } from "../AiChat.types";
import { useAiChatDocumentViewer } from "../documentViewer/AiChatDocumentViewerContext";
import AiChatCitationPopover from "./AiChatCitationPopover";

interface AiChatCitationChipProps {
    source: AiChatSourceReference;
    index: number;
    citationKey: string;
}

const HOVER_CLOSE_DELAY_MS = 120;

function AiChatCitationChip({ source, index, citationKey }: AiChatCitationChipProps) {
    const theme = useTheme();
    const { activeCitationKey, openCitation, closeForEntityLinkNavigation } = useAiChatDocumentViewer();
    const isActive = activeCitationKey === citationKey;
    const anchorRef = useRef<HTMLSpanElement | null>(null);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [popoverOpen, setPopoverOpen] = useState(false);

    function cancelScheduledClose() {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }

    function scheduleClose() {
        cancelScheduledClose();
        closeTimerRef.current = setTimeout(() => setPopoverOpen(false), HOVER_CLOSE_DELAY_MS);
    }

    function openNow() {
        cancelScheduledClose();
        setPopoverOpen(true);
    }

    function handleClick(event: React.MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        setPopoverOpen(false);
        openCitation(source, citationKey);
    }

    return (
        <>
            <Box
                ref={anchorRef}
                component="span"
                onClick={handleClick}
                onMouseEnter={openNow}
                onMouseLeave={scheduleClose}
                onFocus={openNow}
                onBlur={scheduleClose}
                tabIndex={0}
                sx={{
                    mr: "2px",
                    ml: "2px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    border: `1px solid ${isActive ? theme.palette.primary.main : theme.palette.grey[400]}`,
                    backgroundColor: isActive ? theme.palette.primary.main + "40" : theme.palette.primary.main + "25",
                    color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                    fontSize: 10,
                    fontWeight: 600,
                    cursor: "pointer",
                    verticalAlign: "top",
                    lineHeight: 1,
                    transition: "all 0.15s ease",
                    "&:hover": {
                        color: theme.palette.primary.main,
                        border: `1px solid ${theme.palette.primary.main}`,
                    },
                }}
            >
                {index + 1}
            </Box>
            <AiChatCitationPopover
                open={popoverOpen}
                anchorEl={anchorRef.current}
                source={source}
                onClose={() => setPopoverOpen(false)}
                onMouseEnter={openNow}
                onMouseLeave={scheduleClose}
                onEntityLinkNavigate={() => {
                    setPopoverOpen(false);
                    closeForEntityLinkNavigation();
                }}
                onOpenPreview={() => {
                    setPopoverOpen(false);
                    openCitation(source, citationKey);
                }}
            />
        </>
    );
}

export default AiChatCitationChip;
